import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Divider,
  CircularProgress
} from '@mui/material';
import { GLTransactionEntry } from '../firebase/glTransactionServices';

interface GLEntryPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  entries: GLEntryPreviewEntry[];
  isSubmitting: boolean;
  transactionDate: Date;
}

export interface GLEntryPreviewEntry {
  accountNumber: string;
  accountName: string;
  debit: number;
  credit: number;
}

const GLEntryPreviewDialog: React.FC<GLEntryPreviewDialogProps> = ({
  open,
  onClose,
  onConfirm,
  entries,
  isSubmitting,
  transactionDate
}) => {
  // Calculate totals
  const totalDebits = entries.reduce((sum, entry) => sum + entry.debit, 0);
  const totalCredits = entries.reduce((sum, entry) => sum + entry.credit, 0);
  
  // Format currency
  const formatCurrency = (amount: number): string => {
    return 'RM ' + amount.toFixed(2);
  };

  // Format date
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-MY', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>GL Entry Preview</DialogTitle>
      <DialogContent>
        <Typography variant="body1" paragraph>
          The following GL entries will be created for the daily closing on {formatDate(transactionDate)}:
        </Typography>
        
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.light' }}>
                <TableCell>Account Number</TableCell>
                <TableCell>Account Name</TableCell>
                <TableCell align="right">Debit (RM)</TableCell>
                <TableCell align="right">Credit (RM)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry, index) => (
                <TableRow key={index}>
                  <TableCell>{entry.accountNumber}</TableCell>
                  <TableCell>{entry.accountName}</TableCell>
                  <TableCell align="right">{entry.debit > 0 ? entry.debit.toFixed(2) : ''}</TableCell>
                  <TableCell align="right">{entry.credit > 0 ? entry.credit.toFixed(2) : ''}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell colSpan={2}><strong>Total</strong></TableCell>
                <TableCell align="right"><strong>{totalDebits.toFixed(2)}</strong></TableCell>
                <TableCell align="right"><strong>{totalCredits.toFixed(2)}</strong></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color={Math.abs(totalDebits - totalCredits) < 0.01 ? 'success.main' : 'error.main'}>
            {Math.abs(totalDebits - totalCredits) < 0.01 
              ? '✓ Entries are balanced (Total Debits = Total Credits)'
              : '⚠️ Entries are NOT balanced! Please review before proceeding.'}
          </Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary">
          Note: These entries will be posted to the General Ledger when you confirm. This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button 
          onClick={onConfirm} 
          variant="contained" 
          color="primary"
          disabled={isSubmitting || Math.abs(totalDebits - totalCredits) >= 0.01}
        >
          {isSubmitting ? <CircularProgress size={24} /> : 'Confirm & Post to GL'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GLEntryPreviewDialog;
